import { z } from 'zod';
import { isValid, isPast } from 'date-fns';
import i18n from 'services/translation/i18n';
import { Country } from 'services/countries';
import { ConfigInputField } from 'services/configuration';
/**
 * Validation formula for names (accepts any letters)
 * @param {string} message - error message to return
 */
export const validateName = (message: string = i18n.t('validation.invalidName')) =>
  z.string().regex(/^[-' a-zA-ZÀ-ÖØ-öø-ÿ]+$/, message);

/**
 * Validation formula for emails
 * @param {string} message - error message to return
 */
export const validateEmail = (message: string = i18n.t('validation.invalidEmail')) => z.email(message);

/**
 * Validation formula for phone numbers
 * @param {string} message - error message to return
 */
export const validatePhone = (message: string = i18n.t('validation.invalidPhone')) =>
  z.string().regex(/^[0-9]+$/, message);

/**
 * Validation formula for postal codes
 * @param {string} message - error message to return
 */
export const validatePostalCode = (message: string = i18n.t('validation.invalidPostalCode')) =>
  z
    .string()
    .trim()
    .regex(/^[a-zA-Z0-9\s-]+$/, message);

/**
 * Validation formula for date
 * @param {string} message - error message to return
 */
export const validateDate = (message: string = i18n.t('validation.invalidDate')) =>
  z
    .string()
    .min(8, message)
    .max(8, message)
    .refine((value) => {
      const tempVal = `${value.substring(0, 2)}/${value.substring(2, 4)}/${value.substring(4)}`;
      const parsedDate = Date.parse(tempVal);
      return isValid(new Date(parsedDate)) && isPast(parsedDate);
    }, i18n.t('validation.invalidDOB'));

export const validateCountry = (countryList: Country[], message: string = i18n.t('validation.invalidCountry')) =>
  z.string().refine((value) => {
    const index = countryList.findIndex((country) => country.description === value);
    return index > -1;
  }, message);

export const validateInput = (currItem: ConfigInputField, formData: { [k: string]: any }) => {
  let errorMsg: any = null;
  if (currItem.required && !formData[currItem.apiParam]) {
    errorMsg = i18n.t('validation.fieldRequired');
  } else if (currItem.minChar && formData[currItem.apiParam] && formData[currItem.apiParam].length < currItem.minChar) {
    errorMsg = i18n.t('survey.minimumCharRequired', { minChar: currItem.minChar });
  } else if (currItem.maxChar && formData[currItem.apiParam] && formData[currItem.apiParam].length > currItem.maxChar) {
    errorMsg = i18n.t('survey.maximumCharAllowed', { maxChar: currItem.maxChar });
  }

  return errorMsg;
};

export const validateInputType = (item: string, type: string, countryList?: Country[]) => {
  let errorMsg: any = null;
  let result;
  switch (type) {
    case 'email':
      result = validateEmail().safeParse(item);
      errorMsg = result.success ? null : result.error.issues[0]?.message;
      break;
    case 'firstName':
    case 'lastName':
    case 'fullName':
      result = validateName().safeParse(item);
      errorMsg = result.success ? null : result.error.issues[0]?.message;
      break;
    case 'number':
      result = validatePhone().safeParse(item);
      errorMsg = result.success ? null : result.error.issues[0]?.message;
      break;
    case 'lookupPostalCode':
      result = validatePostalCode().safeParse(item);
      errorMsg = result.success ? null : result.error.issues[0]?.message;
      break;
    case 'date':
      result = validateDate().safeParse(item);
      errorMsg = result.success ? null : result.error.issues[0]?.message;
      break;
    case 'countryName':
      result = validateCountry(countryList as Country[]).safeParse(item);
      errorMsg = result.success ? null : result.error.issues[0]?.message;
      break;
    default:
      break;
  }

  return errorMsg;
};
